// One place that answers two questions:
//
//   1. "Who is signed in?"  -> getSessionUser(). Never calls GET /auth/v1/user.
//   2. "This auth call failed. Is the session gone?" -> classifyAuthFailure().
//
// Why (1) exists: supabase-js `auth.getUser()` always makes a network call to
// GET /auth/v1/user, and when no session has been restored yet it sends the
// anon publishable key as the bearer token. The anon key is a JWT with no
// `sub` claim, so GoTrue answers 403 "invalid claim: missing sub claim" and the
// caller concludes nobody is signed in. On a Lovable preview surface the
// session is restored through an async postMessage broker
// (src/integrations/supabase/previewAuthStorage.ts, up to ~2.25s), so that race
// is wide open. getSession() waits for that restore and never asks the server.
//
// Why (2) exists: four different failures used to produce one outcome ("log
// them out"). They are not the same thing. See classifyAuthFailure below.

import { supabase } from '@/integrations/supabase/client';
import { isAbortError } from '@/lib/authQueries';

export type AuthFailureKind =
  /** 401 / invalid_grant — the session really is dead. Sign out cleanly. */
  | 'session_gone'
  /** 403 bad_jwt — wrong token presented. A bug. Clear storage once, sign in again. */
  | 'bad_token'
  /** 5xx, network failure, anything unlabelled — nothing is known. Retry. */
  | 'unknown'
  /** Navigation aborted the request. Expected. Ignore entirely. */
  | 'aborted';

const statusOf = (err: any): number | undefined => {
  const s = err?.status ?? err?.statusCode ?? err?.originalError?.status ?? err?.response?.status;
  const n = typeof s === 'string' ? Number(s) : s;
  return Number.isFinite(n) ? (n as number) : undefined;
};

const textOf = (err: any): string =>
  [err?.code, err?.error, err?.error_code, err?.message, err?.error_description, err?.details]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export function classifyAuthFailure(err: any): AuthFailureKind {
  if (!err) return 'unknown';
  if (isAbortError(err)) return 'aborted';

  const status = statusOf(err);
  const text = textOf(err);

  // The session is genuinely over.
  if (
    status === 401 ||
    /invalid_grant|refresh_token_not_found|refresh token not found|session_not_found|session from session_id claim/.test(
      text
    )
  ) {
    return 'session_gone';
  }

  // The token we sent was the wrong token. Our bug, not their logout.
  if (
    status === 403 ||
    /bad_jwt|missing sub claim|signature is invalid|invalid claim/.test(text)
  ) {
    return 'bad_token';
  }

  // Server trouble or no answer at all. We know nothing.
  return 'unknown';
}

/** The single line the console prints when the client decides to eject someone. */
export function logAuthDecision(
  kind: AuthFailureKind,
  where: string,
  detail?: any
): AuthFailureKind {
  const outcome =
    kind === 'session_gone'
      ? 'SIGN OUT (session really gone)'
      : kind === 'bad_token'
        ? 'RECOVER (wrong token — clearing stored session once, then re-login)'
        : kind === 'unknown'
          ? 'STAY (server/network error — retrying, not signing out)'
          : 'IGNORE (request aborted by navigation)';
  const line = `[auth-exit] ${outcome} | reason=${kind} | at=${where}`;
  if (kind === 'unknown' || kind === 'aborted') console.warn(line, detail ?? '');
  else console.error(line, detail ?? '');
  return kind;
}

/**
 * Who is signed in, without asking the server.
 * Shape-compatible with `supabase.auth.getUser()` so it is a drop-in.
 */
export async function getSessionUser(client: any = supabase) {
  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { data: { user: null }, error };
    return { data: { user: data?.session?.user ?? null }, error: null };
  } catch (error) {
    return { data: { user: null }, error };
  }
}

/** The access token of the current session, or null. Never hits /user. */
export async function getAccessToken(client: any = supabase): Promise<string | null> {
  const { data } = await client.auth.getSession();
  return data?.session?.access_token ?? null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Backoff used for refreshes that failed for unknown reasons. */
export const REFRESH_BACKOFF_MS = [500, 1500, 3500];

export type RefreshResult =
  | { status: 'ok'; session: any }
  | { status: 'no-session' }
  | { status: 'failed'; kind: AuthFailureKind; error: any };

/**
 * Refresh the session. A 5xx, a network error or an abort is retried with
 * backoff — it is never treated as a logout. Only 401 / invalid_grant ends the
 * session, and a 403 bad_jwt is reported as a bug to recover from.
 */
export async function refreshSessionWithRetry(
  client: any = supabase,
  { attempts = REFRESH_BACKOFF_MS.length + 1, wait = sleep } = {}
): Promise<RefreshResult> {
  let last: any = null;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    let error: any = null;
    let data: any = null;
    try {
      const res = await client.auth.refreshSession();
      data = res?.data;
      error = res?.error ?? null;
    } catch (thrown) {
      error = thrown;
    }

    if (!error) {
      if (data?.session) return { status: 'ok', session: data.session };
      return { status: 'no-session' };
    }

    last = error;
    const kind = classifyAuthFailure(error);
    if (kind === 'session_gone' || kind === 'bad_token') {
      return { status: 'failed', kind, error };
    }
    // 'unknown' and 'aborted': nothing is known. Wait and ask again.
    if (attempt < attempts) {
      console.warn(
        `[auth-exit] refresh attempt ${attempt} failed (${kind}) — retrying in ${REFRESH_BACKOFF_MS[attempt - 1]}ms`,
        error
      );
      await wait(REFRESH_BACKOFF_MS[attempt - 1]);
    }
  }

  return { status: 'failed', kind: 'unknown', error: last };
}

export type RecoveryOutcome = 'recovered' | 'gone' | 'unknown';

/**
 * supabase-js ends a session locally for reasons that are not always a logout
 * (a 403 bad_jwt, a 5xx on /token, a token this project did not sign). When an
 * unexpected SIGNED_OUT arrives we try once to put the last known session back
 * using its refresh token. Only a definite "session_gone" answer ejects anyone.
 */
export async function recoverSessionFrom(
  client: any,
  last: { access_token?: string; refresh_token?: string } | null | undefined
): Promise<{ outcome: RecoveryOutcome; session?: any; error?: any }> {
  if (!last?.refresh_token) return { outcome: 'gone' };
  try {
    const { data, error } = await client.auth.setSession({
      access_token: last.access_token ?? '',
      refresh_token: last.refresh_token,
    });
    if (!error && data?.session) return { outcome: 'recovered', session: data.session };
    const kind = classifyAuthFailure(error);
    if (kind === 'session_gone') return { outcome: 'gone', error };
    if (kind === 'bad_token') return { outcome: 'gone', error };
    return { outcome: 'unknown', error };
  } catch (error) {
    const kind = classifyAuthFailure(error);
    return { outcome: kind === 'session_gone' ? 'gone' : 'unknown', error };
  }
}



/** Stored auth entries get cleared at most once per page load. No loops. */
let clearedOnce = false;

export function clearStoredSessionOnce(): boolean {
  if (clearedOnce) return false;
  clearedOnce = true;
  try {
    const keys = Object.keys(localStorage).filter(
      (k) => k.startsWith('sb-') || k === 'rlevents-auth' || k.endsWith('-auth-token')
    );
    keys.forEach((k) => localStorage.removeItem(k));
    console.error('[auth-exit] cleared stored session entries:', keys);
  } catch {
    /* storage unavailable — nothing to clear */
  }
  return true;
}

/** Test seam. */
export function __resetAuthSessionState() {
  clearedOnce = false;
}

export type AuthFailureAction = 'sign-out' | 'recover' | 'retry' | 'ignore';

/**
 * The four outcomes, in one place.
 *   session_gone -> sign out cleanly (caller sends them to the login screen)
 *   bad_token    -> clear the stored session once, then sign in again
 *   unknown      -> retry; the user stays where they are
 *   aborted      -> ignore
 */
export function handleAuthFailure(
  err: any,
  where: string,
  opts: { signOut?: () => void | Promise<void> } = {}
): AuthFailureAction {
  const kind = classifyAuthFailure(err);
  logAuthDecision(kind, where, err);

  if (kind === 'session_gone') {
    void opts.signOut?.();
    return 'sign-out';
  }
  if (kind === 'bad_token') {
    clearStoredSessionOnce();
    void opts.signOut?.();
    return 'recover';
  }
  if (kind === 'unknown') return 'retry';
  return 'ignore';
}
